import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { APP_HOME } from './constants/routes';
import { ThemeProvider } from './context/ThemeContext';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import ApplicationForm from './components/ApplicationForm';
import PwaInstallBanner from './components/PwaInstallBanner';
import PinLockOverlay from './components/PinLockOverlay';
import { isFormsSubdomain } from './config';
import './App.css';

// В development — 30 сек для теста блокировки; в production — 20 мин
const IDLE_MS = process.env.NODE_ENV === 'development'
  ? 30 * 1000
  : 20 * 60 * 1000;

/** Старые ссылки /dashboard/... и .../calls-schedule → новые URL */
function LegacyDashboardRedirect() {
  const { pathname, search, hash } = useLocation();
  if (pathname === '/dashboard' || pathname === '/dashboard/') {
    return <Navigate to={`${APP_HOME}${search}${hash}`} replace />;
  }
  let path = pathname.replace(/^\/dashboard/, '/discordmoderation');
  path = path.replace('/calls-schedule', '/calendar');
  return <Navigate to={`${path}${search}${hash}`} replace />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const hiddenSinceRef = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionExpiresAt');
    sessionStorage.removeItem('sessionLocked');
    setIsAuthenticated(false);
    setLocked(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  // Отмечаем, что сессия продолжается (вкладка не закрывалась) — после входа и после разблокировки
  useEffect(() => {
    if (isAuthenticated && !locked) {
      sessionStorage.setItem('sessionContinues', '1');
    }
  }, [isAuthenticated, locked]);

  useEffect(() => {
    const el = document.documentElement;
    if (isAuthenticated) el.classList.add('dashboard-view');
    else el.classList.remove('dashboard-view');
    return () => el.classList.remove('dashboard-view');
  }, [isAuthenticated]);

  // Блокировка по неактивности (20 мин): не выходим из аккаунта, показываем ввод PIN
  useEffect(() => {
    if (!isAuthenticated || locked) return;

    // Сбрасываем время активности при старте отслеживания (после входа / после разблокировки)
    lastActivityRef.current = Date.now();

    const updateActivity = () => { lastActivityRef.current = Date.now(); };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(ev => window.addEventListener(ev, updateActivity));

    // Проверяем каждые 30 сек, чтобы блокировка сработала в течение 30 сек после 20 мин простоя
    const intervalId = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_MS) {
        sessionStorage.setItem('sessionLocked', '1');
        setLocked(true);
      }
    }, 30 * 1000);

    return () => {
      events.forEach(ev => window.removeEventListener(ev, updateActivity));
      clearInterval(intervalId);
    };
  }, [isAuthenticated, locked]);

  // При сворачивании вкладки/приложения и возврате — требовать PIN (на телефоне таймер в фоне не идёт)
  useEffect(() => {
    if (!isAuthenticated || locked) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now();
      }
      if (document.visibilityState === 'visible' && hiddenSinceRef.current !== null) {
        const hiddenDuration = Date.now() - hiddenSinceRef.current;
        hiddenSinceRef.current = null;
        // Вкладка в фоне/недавно открытых: PIN только если не заходили 20+ минут
        if (hiddenDuration >= IDLE_MS) {
          sessionStorage.setItem('sessionLocked', '1');
          setLocked(true);
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isAuthenticated, locked]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await fetch('/api/auth/verify', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          setIsAuthenticated(true);
          // Вкладка была закрыта и открыта снова — сразу просим PIN (sessionStorage очищается при закрытии вкладки)
          const sessionContinues = sessionStorage.getItem('sessionContinues');
          if (sessionStorage.getItem('sessionLocked') === '1' || !sessionContinues) {
            setLocked(true);
            sessionStorage.setItem('sessionLocked', '1');
          }
        } else {
          sessionStorage.removeItem('sessionLocked');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('sessionExpiresAt');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('sessionExpiresAt');
        sessionStorage.removeItem('sessionLocked');
      }
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    );
  }

  // Если мы на поддомене форм, показываем только формы
  if (isFormsSubdomain()) {
    return (
      <ThemeProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route path="/:code" element={<ApplicationForm />} />
              <Route path="*" element={
                <div className="form-not-found">
                  <div className="form-not-found-inner">
                    <p className="form-not-found-title">Ссылка на форму некорректна</p>
                    <p className="form-not-found-desc">Проверьте корректность ссылки. Должно быть:</p>
                    <code className="form-not-found-example">forms.gta5rpmoders.ru/xxx</code>
                  </div>
                </div>
              } />
            </Routes>
          </div>
        </Router>
      </ThemeProvider>
    );
  }

  // Основное приложение (админка)
  // Когда требуется PIN — показываем только экран ввода PIN, сайт не грузим
  if (isAuthenticated && locked) {
    return (
      <ThemeProvider>
        <div className="App">
          <PinLockOverlay
            onUnlock={() => {
              sessionStorage.removeItem('sessionLocked');
              setLocked(false);
              lastActivityRef.current = Date.now();
            }}
            onLogout={handleLogout}
          />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <div className="App">
          <PwaInstallBanner />
          <Routes>
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? (
                <Login setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to={APP_HOME} />
              )
            } 
          />
          <Route 
            path="/register" 
            element={
              !isAuthenticated ? (
                <Register setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to={APP_HOME} />
              )
            } 
          />
          <Route
            path="/dashboard/*"
            element={<LegacyDashboardRedirect />}
          />
          <Route
            path="/discordmoderation/calls-schedule"
            element={<Navigate to="/discordmoderation/calendar" replace />}
          />
          <Route 
            path="/discordmoderation/*" 
            element={
              isAuthenticated ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route path="/" element={<Navigate to={isAuthenticated ? APP_HOME : "/login"} />} />
          <Route path="/:code" element={<ApplicationForm />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;

